import React, {
  CSSProperties,
  ReactElement,
  useEffect,
  useMemo,
  useRef
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalFuncProps } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { EIconSize, ModalPadding, ModalWidth } from 'utils/constants/app/ui';
import { isMobile } from 'utils/is-mobile';
import { CustomHeader } from './CustomHeader';
import { GlobalIcon } from 'components/GlobalIcon';
import { CustomModal } from 'newcomponents/UI/CustomModal';
import { usePopupScrollDisable } from 'hooks/usePopupScrollDisable';
import { useSearchParams } from 'hooks/useSearchParams';
import { setOpenedModals } from 'store/actions/app-data';
import { getOpenedModals } from 'store/selectors/app-data';
import { getBetslipOpen } from 'store/selectors/betslip';
import './index.less';

type Props = ModalFuncProps & {
  title?: string | React.ReactNode | null;
  visible: boolean;
  closable?: boolean;
  onCancel?: (arg0: React.UIEvent<HTMLElement, MouseEvent>) => void;
  image?:
    | {
        lib: string;
        name: string;
        theme: string;
        size?: number;
        color?: string;
      }
    | false;
  children: React.ReactNode;
  closeIcon?: React.ReactNode;
  maxWidth?: number | string;
  align?: 'left' | 'center' | 'right';
  maskClosable?: boolean;
  padding?: number;
  wrapperPadding?: number;
  width?: number | string;
  centered?: boolean;
  bodyStyle?: Record<string, number | string>;
  style?: Record<string, number | string>;
  maskStyle?: Record<string, number | string>;
  zIndex?: number;
  contentPadding?: number | string;
  className?: string;
  renderInBody?: boolean;
  preventScrollDisable?: boolean;
  hideBorder?: boolean;
  destroyOnClose?: boolean;
  getContainer?: string | false;
  wrapperContainerPadding?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  fullPage?: boolean;
  borderRadius?: string;
  footer?: ReactElement | null;
  closeIconStyle?: CSSProperties;
  fixBody?: boolean;
  renderCloseIconInMask?: boolean;
  customHeader?: {
    title: string;
    iconLib: string;
    iconName: string;
    iconTheme: string;
    onClose: () => void;
  };
};

export const Modal = ({
  title,
  image,
  children,
  zIndex,
  align = 'left',
  maxWidth = ModalWidth.MAX_1272,
  padding,
  wrapperPadding,
  bodyStyle,
  style,
  closeIcon,
  visible,
  preventScrollDisable = false,
  hideBorder,
  destroyOnClose = true,
  fixBody = true,
  customHeader,
  contentPadding,
  wrapClassName,
  getContainer,
  wrapperContainerPadding,
  placement,
  fullPage,
  footer,
  maskStyle,
  borderRadius,
  closeIconStyle,
  ...rest
}: Props): ReactElement => {
  const dispatch = useDispatch();
  const openedModals = useSelector(getOpenedModals);
  const open = useSelector(getBetslipOpen);
  const modalId = useRef(uuidv4());
  const { accounts } = useSearchParams();

  const _preventScrollDisable = useMemo(() => {
    return (
      !!accounts ||
      openedModals.size > 0 ||
      (open && isMobile()) ||
      preventScrollDisable
    );
  }, [accounts, openedModals.size, open, preventScrollDisable]);

  usePopupScrollDisable(visible, _preventScrollDisable, fixBody);

  const updateOpenedModal = (updateType: 'add' | 'delete') => {
    if (openedModals.size) {
      const openedModalsCopy = new Set(openedModals);
      openedModalsCopy[updateType](modalId.current);
      dispatch(setOpenedModals(openedModalsCopy));
    }
  };

  useEffect(() => {
    if (visible) {
      updateOpenedModal('add');

      return () => {
        updateOpenedModal('delete');
      };
    }
  }, [visible]);

  return (
    <CustomModal
      {...rest}
      hideBorder={hideBorder || false}
      wrapClassName={wrapClassName || ''}
      zIndex={zIndex || 0}
      borderRadius={borderRadius}
      maxWidth={maxWidth || ''}
      destroyOnClose={destroyOnClose || false}
      getContainer={getContainer || ''}
      wrapperPadding={wrapperContainerPadding}
      placement={placement}
      fullPage={fullPage}
      contentPadding={customHeader ? 0 : contentPadding ? contentPadding : 0}
      footer={footer}
      maskStyle={maskStyle}
      bodyStyle={
        bodyStyle || {
          padding: `${customHeader ? 0 : padding ?? ModalPadding.MOBILE_12}px`,
          textAlign: align
        }
      }
      style={
        style || {
          borderRadius: '12px',
          padding: `${wrapperPadding ?? ModalPadding.WRAPPER_8}px`
        }
      }
      closeIcon={
        closeIcon || (
          <GlobalIcon
            lib="generic"
            name="closeFill"
            theme="default"
            size={EIconSize._24}
            className="modal__close-icon"
          />
        )
      }
      visible={visible}
      closeIconStyle={closeIconStyle}
    >
      {customHeader && (
        <CustomHeader
          iconName={customHeader.iconName}
          iconTheme={customHeader.iconTheme}
          iconLib={customHeader.iconLib}
          title={customHeader.title}
          onCancel={() => {
            if (customHeader.onClose) {
              customHeader.onClose();
            }
          }}
        />
      )}
      {image && (
        <div className="modalImageWrapper">
          <GlobalIcon
            lib={image.lib}
            name={image.name}
            theme={image.theme}
            size={image.size ? image.size : EIconSize._72}
            color={image.color}
            className="modal__icon"
          />
        </div>
      )}
      {!!title && <div className="modal-title">{title}</div>}
      <div className="modal__body">{children}</div>
    </CustomModal>
  );
};

export const ModalButtons = ({
  direction = 'row',
  children,
  className,
  ...props
}: {
  direction?: 'row' | 'column';
  children: React.ReactNode;
  className?: string;
}): ReactElement => {
  return (
    <div
      className={`modal__footer ${className}`}
      style={{ flexDirection: direction }}
      {...props}
    >
      {children}
    </div>
  );
};
